import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import Button from '../../../components/Button'
import DateButton from '../../../components/DateButton'
import {
  fetchFullActivityReport
} from '../../../services/api'

function transformReportToState(report) {
  const practices = {}

  report.forEach(row => {
    const {
      practice_name,
      provider_name,
      sent_count,
      from_providers_count,
      from_patients_count,
    } = row

    // setting or appending practice information
    if (!practices[practice_name]) {
      practices[practice_name] = {
        sent: parseInt(sent_count),
        receivedProviders: parseInt(from_providers_count),
        receivedPatietns: parseInt(from_patients_count),
        showProviders: false,
        providers: {},
      }
    } else {
      practices[practice_name].sent += parseInt(sent_count)
      practices[practice_name].receivedProviders += parseInt(from_providers_count)
      practices[practice_name].receivedPatietns += parseInt(from_patients_count)
    }

    // setting provider information
    practices[practice_name].providers[provider_name] = {
      sent: parseInt(sent_count),
      receivedProviders: parseInt(from_providers_count),
      receivedPatietns: parseInt(from_patients_count),
    }
  })

  return practices
}

function isValidDate(d) {
  return new Date(d) instanceof Date && !isNaN(new Date(d));
}

function ActivityReports(props) {
  const {
    classes: {
      looksLoading,
      datePickers,
      expandButton,
    }
  } = props
  /**
   * practices data structure:
   * 
   * {
   *    [PRACTICE_NAME]: {
   *        sent: int,
   *        receivedProviders: int,
   *        receivedPatietns: int,
   *        showProviders: bool,
   *        providers: {
   *           [PROVIDER_NAME]: {
   *              sent: int,
   *              receivedProviders: int,
   *              receivedPatietns: int,
   *           },
   *           ...
   *        },
   *        ...
   *    }
   * }
   * 
   * NOTE:
   * It is important for sorting that the keys with
   * numerical values on the practices and the providers match
   */
  const [practices, setPractices] = useState({})
  const [sortedOrder, setSortedOrder] = useState('desc')
  const [sortedBy, setSortedByProperty] = useState('sent')
  const [startDate, setStartDate] = useState(moment().subtract(30, 'day').format('MM-DD-YYYY'))
  const [endDate, setEndDate] = useState(moment().format('MM-DD-YYYY'))
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    (async () => {
      setIsLoading(true)
      const report = await fetchFullActivityReport(startDate, endDate)
      const allPractices = transformReportToState(report)
      setPractices(allPractices)
      setIsLoading(false)
    })();
  }, [startDate, endDate])

  const toggleIfBreakdownVisable = (practiceName) => () => {
    setPractices({
      ...practices,
      [practiceName]: {
        ...practices[practiceName],
        showProviders: !practices[practiceName].showProviders
      }
    })
  }

  const sortedDictKeys = (dict) => {
    return Object.keys(dict).sort((key1, key2) => {
      let order = dict[key1][sortedBy] - dict[key2][sortedBy]
      return sortedOrder === 'desc' ? -order : order
    })
  }

  const handleSortingChange = (newSortedByProperty) => () => {
    const newOrder = newSortedByProperty === sortedBy ? sortedOrder === 'desc' ? 'asc' : 'desc' : 'desc' // toggle or default to desc
    setSortedByProperty(newSortedByProperty)
    setSortedOrder(newOrder)
  }

  const renderSortableTableHeadCell = (label, sortType) => (
    <TableCell align="right">
      <div
        style={{
          display: 'flex',
          cursor: 'pointer',
          fontWeight: 'bold',

        }}
        onClick={handleSortingChange(sortType)}
      >
      {label}
      {sortType === sortedBy && 
        <TableSortLabel
          active={sortType === sortedBy}
          direction={sortedOrder}
        />
      }
      </div>
    </TableCell>
  )

  const renderPracticeRow = (practiceName) => {
    const practice = practices[practiceName]
  
    return (
      <>
        <TableRow style={{'& > *': { borderBottom: 'unset' }}}>
          <TableCell align="left">
            <Button aria-label="expand row" size="small" className={expandButton} onClick={toggleIfBreakdownVisable(practiceName)}>
              {practice.showProviders ? "Collapse" : "Expand"}
            </Button>
          </TableCell>
          <TableCell component="th" scope="row">{practiceName}</TableCell>
          <TableCell align="right">{practice.sent}</TableCell>
          <TableCell align="right">{practice.receivedProviders}</TableCell>
          <TableCell align="right">{practice.receivedPatietns}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={practice.showProviders} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom component="div">
                  Providers
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Sent</TableCell>
                      <TableCell align="right">Received from referrals</TableCell>
                      <TableCell align="right">Received from website</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedDictKeys(practice.providers).map(providerName => {
                      const provider = practice.providers[providerName]
                      return (
                        <TableRow key={providerName}>
                          <TableCell component="th" scope="row">{providerName}</TableCell>
                          <TableCell align="right">{provider.sent}</TableCell>
                          <TableCell align="right">{provider.receivedProviders}</TableCell>
                          <TableCell align="right">{provider.receivedPatietns}</TableCell>
                        </TableRow>
                      )}
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  return (
    <>
    <div className={datePickers}>
      <Typography style={{ margin: '0 10px'  }}>From</Typography>
      <DateButton
        disableFuture
        selectedDate={startDate}
        handleDateChange={(date) => {
          if (date && isValidDate(date)) {
            setStartDate(moment(date).format('MM-DD-YYYY'))
          }
        }}
      />
      <Typography style={{ margin: '0 10px 0 25px'  }}>To</Typography>
      <DateButton
        disableFuture
        selectedDate={endDate}
        handleDateChange={(date) => {
          if (date && isValidDate(date)) {
            setEndDate(moment(date).format('MM-DD-YYYY'))
          }
        }}
      />
    </div>
    <TableContainer component={Paper} className={isLoading ? looksLoading  : ''}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell align="left">See More</TableCell>
            <TableCell>Practice</TableCell>
            {renderSortableTableHeadCell("Sent", "sent")}
            {renderSortableTableHeadCell("Received from referrals", "receivedProviders")}
            {renderSortableTableHeadCell("Received from website", "receivedPatietns")}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedDictKeys(practices).map(renderPracticeRow)}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
}

const styles = {
  looksLoading: {
    opacity: '0.5'
  },
  datePickers: {
    display: 'flex',
    width: '100%',
    alignItems: 'center'
  },
  expandButton: {
    border: '1px solid #d4d4d4'
  }
}

export default withStyles(styles)(ActivityReports);